import { useElementSize } from '@mantine/hooks';
import { FC, useContext, useEffect, useState } from 'react';
import { Col, Row } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { Anchor, Button, Space, Text as MantineText } from '@mantine/core';
import { IconCalendarTime } from '@tabler/icons';
import BookitupAddIcon from '../../../Atoms/BookitupAddIcon';
import BookitupTable from '../../../Atoms/BookitupTable';
import PanePaper from '../../../Molecules/PanePaper';
import ContactFormsNotFound from '../../../Organismns/Tables/ContactFormsNotFound';
import { ViolationContext } from '../../../Service/Context/ViolationContext';
import { LicenceS } from '../../../Service/LicenceS';
import SemanticForm from '../../../Types/SemanticForm';
import SkeletonItems from '../../../Atoms/SkeletonItems';
import { SemanticFormS } from '../../../Service/restapi/semanticFormService';
import { RoutingS } from '../../../Service/RoutingS';
import BookitupTutorialIcon from '../../../Atoms/BookitupTutorialIcon';
import CodeWithCopy from '../../../Atoms/CodeWithCopy';
import { ProfileContext } from '../../../Service/Context/ProfileContext';
import ContactFormListItem from './ContactFormListItem';
import { openCreateContactFormModal } from './CreateContactFormModal';

const ContactFormList: FC = () => {
  const navigate = useNavigate();
  const { profile } = useContext(ProfileContext);
  const { ref, width } = useElementSize();
  const { executeWithLicenceCheck } = useContext(ViolationContext);
  const [forms, setForms] = useState<SemanticForm[]>([]);
  const [loading, setLoading] = useState(false);
  const { length } = forms;
  const profileId = profile?.id?.toString() ?? '';
  const aiContactFormPrompt = `Bitte passe das bestehende Kontaktformular so an, dass nach dem Absenden automatisch ein POST-Request an folgende URL gesendet wird:

https://ingest.bookitup.de/profile/${profileId}

Wichtige Anforderungen:

- Der Request muss per fetch() als POST gesendet werden

- Der Body muss reines JSON sein

- Der Header Content-Type muss EXAKT diesen Wert haben:

application/json

- Es darf KEIN charset=utf-8 oder ähnlicher Zusatz enthalten sein

- Alle Formularfelder sollen automatisch übernommen werden

- Falls bei einem Feld eine Mehrfachauswahl (z.B. durch Checkboxen) übertragen werden soll, müssen die einzlenen Werte durch ein Semicolon getrennt werden.

- Es soll nur das Anfrageformular verknüpft werden.

- Zusätzlich muss IMMER folgendes Feld mitgesendet werden:

pathId: window.location.hostname + "#kontakt"

Beispiel für den finalen JSON-Body:

{

"email": "test@web.de",

"date": "16.04.2027",

"name": "Max Mustermann",

"message": "Hallo",

"pathId": "meinefirma.de#kontakt"

}

Technische Anforderungen:

- Nutze async/await

- Nutze fetch()

- Keine externe Library verwenden

- Fehler sauber behandeln

- Erfolgreiche Übermittlung anzeigen

- Kein Page Reload

- Der bestehende Form-Aufbau und das Styling sollen unverändert bleiben

- Der Code soll production-ready und sauber strukturiert sein

Bitte implementiere die vollständige Formularlogik inklusive Submit-Handler.`;

  useEffect(() => {
    fetchForms();
    RoutingS.changeTitle('Kontaktformulare');
  }, []);

  const fetchForms = () => {
    setLoading(true);
    SemanticFormS.getForms()
      .then(setForms)
      .finally(() => setLoading(false));
  };

  const showColumns = {
    showDatum: width > 400,
    showName: width > 150,
    showCount: width > 300,
    narrowActions: true,
  };

  const onClick = () => {
    if (length > 0) {
      executeWithLicenceCheck(LicenceS.Restrictions.CONTACT_FORM, openCreateContactFormModal);
    } else {
      openCreateContactFormModal();
    }
  };

  return (
    <>
      <PanePaper
        title="Kontaktformulare"
        rightSection={
          <>
            <BookitupTutorialIcon
              videoUrl="https://app.bookitup.de/academy/channels/automatic-responses?episodeId=9yar1sis4n"
              name="CreateContactForm"
            />
            <BookitupAddIcon onClick={onClick} />
          </>
        }
        sx={{ maxWidth: 900 }}
      >
        Erstelle Dir mit wenigen Klicks ein Kontaktformular und füge es auf Deiner Webseite als HTML-Baustein hinzu.
        <Space h="xl" />
        <div ref={ref}>
          {loading && <SkeletonItems height={40} />}
          {!loading && length === 0 && <ContactFormsNotFound message="Keine Formulare gefunden." onClick={onClick} />}
          {!loading && length > 0 && (
            <BookitupTable>
              <thead>
                <tr>
                  {showColumns.showName && <th>Bezeichnung </th>}
                  {showColumns.showDatum && <th>Erstellungsdatum</th>}
                  {showColumns.showCount && <th>Einsendungen</th>}
                  <th> </th>
                </tr>
              </thead>
              <tbody>
                {forms.map((form: SemanticForm) => (
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  <ContactFormListItem key={form.id} form={form} reload={fetchForms} {...showColumns} />
                ))}
              </tbody>
            </BookitupTable>
          )}
        </div>
      </PanePaper>
      <Space h="sm" />
      <PanePaper title="Eigenes Kontaktformular verbinden lassen" sx={{ maxWidth: 900 }}>
        <Row>
          <Col xs="12">
            <MantineText>
              Neben der Möglichkeit ein Kontaktformular in bookitup zu erstellen und auf Deiner Webseite einzubinden,
              gibt es auch die Möglichtkeit Dein bereits vorhandenes Kontaktformular mit bookitup zu verbinden. Das
              bieten wir insbesondere dann an, wenn Du ein Kontaktformular hast, dass sich nicht mit unserem
              Kontaktformular-Editor nachbauen lässt; beispielsweise Dein Kontaktformular auch Bilder enthalten soll,
              mehrschrittig ist oder Dir dein Kontaktformular einfach besser gefällt.
              <Space h="sm" />
              Die Anbindung erfolgt dann meistens über einen Webhook - oder über Tools wie Zapier.
              <Space h="sm" />
              Buche Dir für die persönliche Einrichtung einfach einen Technik-Termin:
              <Space h="sm" />
            </MantineText>
            <Button
              size="xs"
              leftIcon={<IconCalendarTime size={16} />}
              mt="md"
              onClick={() =>
                RoutingS.openInNewTab('https://hey.bookitup.de/meetings/sebastian-mahlke/technik-support ')
              }
            >
              Termin vereinbaren
            </Button>

            <MantineText size="xs" mt={20} mb={10}>
              Wenn wir dein Kontaktformular dann zusammen verbinden brauchen wir deine Kundennummer:
            </MantineText>
            <div style={{ maxWidth: 200 }}>
              <CodeWithCopy content={profileId} />
            </div>
          </Col>
        </Row>
      </PanePaper>
      <Space h="sm" />
      <PanePaper title="KI-Kontaktformular verbinden" sx={{ maxWidth: 900 }}>
        <MantineText>
          Falls du deine Webseite mit einer KI wie Claude gebaut hast, kannst du dein Kontaktformular mit folgendem
          Prompt mit bookitup verbinden.
        </MantineText>
        <Space h="md" />
        <MantineText size="sm">
          Wenn die KI das gemacht hat, schick das Formular noch einmal ab und gehe dann in bookitup unter{' '}
          <Anchor onClick={() => navigate('/settings/connectwebsite')}>Webseite verbinden</Anchor> und wähle oben im
          Dropdown z.B. Sonstiges aus. Danach findest Du ganz unten ein neues &quot;Verbundenes Formular&quot;. Wenn Du
          dort einmal drauf klickst, werden Dir die Zuordnungen der Felder Deiner Webseite (links) zu den Feldern von
          bookitup (rechts) angezeigt.
        </MantineText>
        <Space h="sm" />
        <MantineText size="sm">
          Für spezielle Infos wie &quot;Personenanzahl&quot; etc. kannst Du Dir{' '}
          <Anchor onClick={() => navigate('/settings/customfields')}>eigene Felder</Anchor> anlegen und diese
          anschließend ebenfalls mit Feldern aus dem Kontaktformular verknüpfen. Wenn Du das Formular dann noch mal
          ausfüllst, wird das Event mit diesen Daten angelegt.
        </MantineText>
        <Space h="sm" />
        <MantineText size="sm">
          Wenn Du jetzt ein weiteres Feld hinzufügst, musst Du das Formular einmal absenden. Danach wird es ebenfalls in
          dieser Liste angezeigt und Du musst es nur noch dem entsprechenden Feld zuweisen. Bei der nächsten Anfrage
          werden diese Daten dann ebenfalls automatisch eingetragen.
        </MantineText>
        <Space h="sm" />
        <CodeWithCopy content={aiContactFormPrompt} />
      </PanePaper>
    </>
  );
};

export default ContactFormList;
